import React, { useState, useEffect } from 'react';
import { API_BASE_URL } from './config.js';
import {
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActionArea,
  Box,
  ThemeProvider,
  createTheme,
  CssBaseline,
  Chip,
  useMediaQuery,
  CircularProgress
} from '@mui/material';
import { Description as PdfIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

// Theme (same as before)
const customTheme = createTheme({
  palette: {
    primary: {
      main: '#1d5576',
    },
    secondary: {
      main: '#f9a91e',
    },
    background: {
      default: '#ffffff',
    }
  },
  typography: {
    h4: {
      fontWeight: 'bold',
    },
    h5: {
      fontWeight: 'bold',
    },
    h6: {
      fontWeight: 'medium',
    }
  },
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 900,
      lg: 1200,
      xl: 1536,
    },
  },
});

const CatalogsPage = () => {
  const [marketingData, setMarketingData] = useState({});
  const [loading, setLoading] = useState(true);
  const isMobile = useMediaQuery(customTheme.breakpoints.down('sm'));
  const navigate = useNavigate();

  useEffect(() => {
    setLoading(false);
  }, []);

  const handleCardClick = (category) => {
    // Navigate to a category page
    navigate(`/catalogs/${category}`, { 
      state: { 
        category,
        items: marketingData[category] || [] 
      } 
    });
  };

  // Define the three main categories we want to show
  const mainCategories = ['In Line', 'Clearance', 'Promotional'];

  if (loading) {
    return (
      <ThemeProvider theme={customTheme}>
        <CssBaseline />
        <Container sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
          <CircularProgress />
        </Container>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={customTheme}>
      <CssBaseline />
      <Container maxWidth="lg" sx={{ py: 4 }}>
        {/* Header Section - Simplified without search */}
        <Box sx={{ 
          mb: 4, 
          textAlign: 'center',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <Typography 
            variant={isMobile ? "h5" : "h4"}
            component="h1" 
            gutterBottom
            sx={{ 
              color: 'primary.main'
            }}
          >
            Catalogs & Pricing
          </Typography>
          <Typography 
            variant={isMobile ? "body1" : "h6"}
            color="text.secondary"
            sx={{ mb: 3, maxWidth: '600px' }}
          >
            Access the recent catalogs and pricing
          </Typography>
        </Box>

        {/* Main Categories Section - Always show the three main categories */}
        <Box sx={{ mb: 6 }}>
          <Typography 
            variant={isMobile ? "h6" : "h5"}
            component="h2" 
            gutterBottom
            sx={{ 
              color: 'secondary.main',
              borderBottom: '2px solid',
              borderColor: 'secondary.main',
              pb: 1,
              mb: 3,
              textAlign: 'left'
            }}
          >
            Categories
          </Typography>
          
          <Grid 
            container 
            spacing={3} 
            sx={{ 
              justifyContent: 'center',
              alignItems: 'center'
            }}
          >
            {mainCategories.map((category) => {
              return (
                <Grid 
                  item 
                  xs={12} 
                  sm={8} 
                  md={6} 
                  lg={4}  // Changed to lg={4} to show 3 tiles in a row
                  key={category}
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center'
                  }}
                >
                  <Card 
                    sx={{ 
                      height: '100%',
                      width: '270px',
                      minHeight: 200,
                      maxWidth: '400px',
                      display: 'flex',
                      flexDirection: 'column',
                      transition: 'all 0.3s ease-in-out',
                      cursor: 'pointer',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: 6,
                      }
                    }}
                  >
                    <CardActionArea 
                      onClick={() => handleCardClick(category)}
                      sx={{ 
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        flexGrow: 1
                      }}
                    >
                      <CardContent sx={{ 
                        textAlign: 'center', 
                        p: isMobile ? 2 : 3,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexGrow: 1,
                        width: '100%'
                      }}>
                        {/* Category icon - using PDF icon as placeholder */}
                        <PdfIcon 
                          sx={{ 
                            fontSize: isMobile ? 40 : 48,
                            color: 'primary.main',
                            mb: 2
                          }} 
                        />
                        
                        <Typography 
                          variant={isMobile ? "subtitle1" : "h6"}
                          component="h3"
                          sx={{ 
                            fontWeight: 'medium',
                            mb: 1,
                            minHeight: isMobile ? '50px' : '60px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: '100%',
                            lineHeight: 1.2,
                            textAlign: 'center'
                          }}
                        >
                          {category}
                        </Typography>
                        
                        <Chip 
                          label="Browse Category"
                          size="small" 
                          variant="outlined"
                          sx={{ 
                            borderColor: 'primary.main',
                            color: 'primary.main'
                          }}
                        />
                      </CardContent>
                    </CardActionArea>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        </Box>

        {/* Optional: Show all items under each category without search */}
        {mainCategories.map(category => {
          const items = marketingData[category] || [];
          if (items.length === 0) return null;
          
          return (
            <Box key={category} sx={{ mb: 6 }}>
              <Typography 
                variant={isMobile ? "h6" : "h5"}
                component="h2" 
                gutterBottom
                sx={{ 
                  color: 'secondary.main',
                  borderBottom: '2px solid',
                  borderColor: 'secondary.main',
                  pb: 1,
                  mb: 3,
                  textAlign: 'left'
                }}
              >
                {category}
              </Typography>
              
              <Grid 
                container 
                spacing={3} 
                sx={{ 
                  justifyContent: 'center',
                  alignItems: 'center'
                }}
              >
                {items.map((item) => (
                  <Grid 
                    item 
                    xs={12} 
                    sm={8} 
                    md={6} 
                    lg={3} 
                    key={item.id}
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center'
                    }}
                  >
                    <Card 
                      sx={{ 
                        height: '100%',
                        width: '270px',
                        minHeight: 200,
                        maxWidth: '400px',
                        display: 'flex',
                        flexDirection: 'column',
                        transition: 'all 0.3s ease-in-out',
                        cursor: 'pointer',
                        '&:hover': {
                          transform: 'translateY(-4px)',
                          boxShadow: 6,
                        }
                      }}
                    >
                      <CardActionArea 
                        onClick={() => navigate(`/brand/${item.id}`, { state: { brand: item } })}
                        sx={{ 
                          height: '100%',
                          display: 'flex',
                          flexDirection: 'column',
                          justifyContent: 'center',
                          flexGrow: 1
                        }}
                      >
                        <CardContent sx={{ 
                          textAlign: 'center', 
                          p: isMobile ? 2 : 3,
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexGrow: 1,
                          width: '100%'
                        }}>
                          {/* Item icon - using PDF icon as placeholder */}
                          <PdfIcon 
                            sx={{ 
                              fontSize: isMobile ? 40 : 48,
                              color: 'primary.main',
                              mb: 2
                            }} 
                          />
                          
                          <Typography 
                            variant={isMobile ? "subtitle1" : "h6"}
                            component="h3"
                            sx={{ 
                              fontWeight: 'medium',
                              mb: 1,
                              minHeight: isMobile ? '50px' : '60px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              width: '100%',
                              lineHeight: 1.2,
                              textAlign: 'center'
                            }}
                          >
                            {item.name}
                          </Typography>
                          <Chip 
                            label="View Details"
                            size="small" 
                            variant="outlined"
                            sx={{ 
                              mb: 1,
                              borderColor: 'primary.main',
                              color: 'primary.main'
                            }}
                          />
                        </CardContent>
                      </CardActionArea>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </Box>
          );
        })}

      </Container>
    </ThemeProvider>
  );
};

export default CatalogsPage;